import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Activity, AlertCircle } from 'lucide-react';

const DeviationStatistics = ({ location, timeframe }) => {
  const deviationData = [
    { type: 'No Flow', identified: 45, mitigated: 38, remaining: 7 },
    { type: 'High Pressure', identified: 32, mitigated: 28, remaining: 4 },
    { type: 'Low Temp', identified: 28, mitigated: 25, remaining: 3 },
    { type: 'High Level', identified: 38, mitigated: 32, remaining: 6 },
    { type: 'Low Level', identified: 22, mitigated: 20, remaining: 2 },
  ];

  const safeguardEffectiveness = [
    { category: 'Detection Systems', effectiveness: 94, count: 156 },
    { category: 'Containment', effectiveness: 89, count: 142 },
    { category: 'Emergency Response', effectiveness: 87, count: 128 },
    { category: 'Preventive Maintenance', effectiveness: 92, count: 167 },
  ];

  const totalIdentified = deviationData?.reduce((sum, item) => sum + item?.identified, 0);
  const totalMitigated = deviationData?.reduce((sum, item) => sum + item?.mitigated, 0);
  const mitigationRate = ((totalMitigated / totalIdentified) * 100)?.toFixed(1);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Deviation Analysis</h2>
        <p className="text-sm text-gray-600 mt-1">
          Statistical breakdown and mitigation progress
        </p>
      </div>
      {/* Summary Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-5 h-5 text-blue-600" />
            <TrendingUp className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-blue-900">{totalIdentified}</div>
          <div className="text-sm text-blue-700 mt-1">Total Deviations</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <AlertCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm font-bold text-green-600">{mitigationRate}%</span>
          </div>
          <div className="text-2xl font-bold text-green-900">{totalMitigated}</div>
          <div className="text-sm text-green-700 mt-1">Mitigated</div>
        </div>
      </div>
      {/* Deviation Types Chart */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Deviation Types & Mitigation Status</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={deviationData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="type" 
                stroke="#6B7280"
                style={{ fontSize: '11px' }}
                angle={-15}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
              />
              <Bar 
                dataKey="identified" 
                fill="#3B82F6" 
                name="Identified"
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="mitigated" 
                fill="#10B981" 
                name="Mitigated"
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="remaining" 
                fill="#EF4444" 
                name="Remaining"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Safeguard Effectiveness */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Safeguard Effectiveness</h3>
        <div className="space-y-3">
          {safeguardEffectiveness?.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700">{item?.category}</span>
                <span className="font-bold text-gray-900">{item?.effectiveness}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    item?.effectiveness >= 90 ? 'bg-green-500' :
                    item?.effectiveness >= 80 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${item?.effectiveness}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500">{item?.count} safeguards implemented</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeviationStatistics;